import Papa from 'papaparse';

const MAX_CSV_BYTES = 5 * 1024 * 1024; // 5 MB
const PREVIEW_ROW_LIMIT = 50;

export function guessColumnMapping(headers) {
  const mapping = { phone: '', name: '', email: '' };

  headers.forEach((header) => {
    const lower = String(header || '').toLowerCase().trim();
    if (
      !mapping.phone &&
      (lower.includes('phone') ||
        lower.includes('mobile') ||
        lower.includes('whatsapp') ||
        lower === 'number' ||
        lower === 'tel' ||
        lower === 'cell')
    ) {
      mapping.phone = header;
      return;
    }
    if (
      !mapping.name &&
      (lower.includes('name') || lower === 'full name' || lower === 'contact')
    ) {
      mapping.name = header;
      return;
    }
    if (!mapping.email && (lower.includes('email') || lower === 'e-mail')) {
      mapping.email = header;
    }
  });

  return mapping;
}

export function cleanAndTrimHeaders(headers) {
  const mapped = (headers || []).map((h) =>
    h === undefined || h === null ? '' : String(h).trim()
  );
  let lastNonEmptyIndex = -1;
  for (let i = mapped.length - 1; i >= 0; i--) {
    if (mapped[i] !== '') {
      lastNonEmptyIndex = i;
      break;
    }
  }
  return mapped.slice(0, lastNonEmptyIndex + 1);
}

function isRowEmpty(row, headers) {
  return headers.every((header) => {
    const value = row?.[header];
    return value === undefined || value === null || String(value).trim() === '';
  });
}

function normalizeParsedRows(headers, rawRows) {
  return (rawRows || [])
    .map((row) => {
      const next = {};
      headers.forEach((header) => {
        const value = row?.[header];
        next[header] =
          value === undefined || value === null ? '' : String(value).trim();
      });
      return next;
    })
    .filter((row) => !isRowEmpty(row, headers));
}

export function validateContactImportCsvFile(file) {
  if (!file) {
    return 'Select a CSV file to import.';
  }

  const name = String(file.name || '').toLowerCase();
  if (!name.endsWith('.csv')) {
    return 'Only CSV files are supported. Please upload a .csv file.';
  }

  if (!file.size) {
    return 'The selected file is empty.';
  }

  if (file.size > MAX_CSV_BYTES) {
    return 'CSV file is too large. Maximum size is 5 MB.';
  }

  return '';
}

export function validateParsedFileData(headers, rows) {
  if (!headers || headers.length === 0) {
    throw new Error(
      'The uploaded file appears to be empty or has no column headers.'
    );
  }

  const hasEmptyHeader = headers.some((h) => !h || !String(h).trim());
  if (hasEmptyHeader) {
    throw new Error(
      'The uploaded file contains empty or missing column header names. Please fix the headers in your file.'
    );
  }

  const seenHeaders = new Set();
  for (const h of headers) {
    const normalized = String(h).trim().toLowerCase();
    if (seenHeaders.has(normalized)) {
      throw new Error(
        `The uploaded file contains duplicate column headers: "${String(h).trim()}". Each column must have a unique header name.`
      );
    }
    seenHeaders.add(normalized);
  }

  if (!rows || rows.length === 0) {
    throw new Error('The uploaded file does not contain any contact data rows.');
  }
}

function parseCsvFile(file) {
  return new Promise((resolve, reject) => {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: 'greedy',
      transformHeader: (header) => String(header || '').trim(),
      complete: (results) => {
        const fatalError = (results.errors || []).find(
          (error) => error?.type === 'Delimiter' || error?.code === 'UndetectableDelimiter'
        );
        if (fatalError) {
          reject(
            new Error(
              fatalError.message ||
                'Could not detect CSV columns. Check the file uses commas and has a header row.'
            )
          );
          return;
        }

        const rawHeaders = results.meta.fields || [];
        const headers = cleanAndTrimHeaders(rawHeaders);
        const rows = normalizeParsedRows(headers, results.data || []);
        resolve({ headers, rows });
      },
      error: (error) =>
        reject(
          error instanceof Error
            ? error
            : new Error(error?.message || 'Failed to parse CSV file.')
        ),
    });
  });
}

async function parseExcelFile(file) {
  const arrayBuffer = await file.arrayBuffer();
  const XLSX = await import('xlsx');
  const workbook = XLSX.read(arrayBuffer, { type: 'array' });
  const sheetName = workbook.SheetNames[0];
  if (!sheetName) {
    throw new Error('Excel workbook has no sheets.');
  }
  const sheet = workbook.Sheets[sheetName];
  const rawRows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' });
  const rawHeaders = rawRows[0] || [];
  const headers = cleanAndTrimHeaders(rawHeaders);
  const rows = normalizeParsedRows(
    headers,
    XLSX.utils.sheet_to_json(sheet, { defval: '' })
  );
  return { headers, rows };
}

/** Contacts import UI is CSV-only; excel remains for other callers (e.g. campaign recipients). */
export async function parseContactImportFile(file, method = 'csv') {
  if (method === 'csv') {
    const fileError = validateContactImportCsvFile(file);
    if (fileError) {
      throw new Error(fileError);
    }
  }

  const parsed =
    method === 'excel' ? await parseExcelFile(file) : await parseCsvFile(file);

  validateParsedFileData(parsed.headers, parsed.rows);
  return parsed;
}

export function getImportPreviewRows(rows, limit = PREVIEW_ROW_LIMIT) {
  return (rows || []).slice(0, limit);
}

export function countRowsWithPhone(rows, phoneHeader) {
  if (!phoneHeader) return 0;
  return (rows || []).filter((row) => String(row?.[phoneHeader] || '').trim())
    .length;
}

export function buildImportContactsMappingPayload(mapping) {
  const payload = {};
  if (mapping.phone) payload.phone = mapping.phone;
  if (mapping.name) payload.name = mapping.name;
  if (mapping.email) payload.email = mapping.email;
  return payload;
}

export { MAX_CSV_BYTES, PREVIEW_ROW_LIMIT };
